import React, { useEffect, useMemo, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR from 'swr';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Plus, Search, ShieldCheck, UserCog } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button, buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { createAdminUser, listAdminUsers, type AdminUserSummary } from '@/api/adminUsers';
import { extractApiError } from '@/utils/apiError';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

const PAGE_SIZE = 50;

export default function UsersListPage() {
  const { t } = useT();
  usePageTitle(t('admin.users.title'), t('admin.users.subtitle'));

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(query.trim());
      setOffset(0);
    }, 200);
    return () => window.clearTimeout(id);
  }, [query]);

  const swrKey = `admin-users:${debounced}:${offset}:${PAGE_SIZE}`;
  const { data, error, isLoading } = useSWR(swrKey, () =>
    listAdminUsers({ q: debounced || undefined, limit: PAGE_SIZE, offset }).then(r => r.data),
  );

  const total = data?.count ?? 0;
  const results = data?.results ?? [];
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  return (
    <div className="container mx-auto max-w-5xl space-y-4 px-4 py-6">
      <Card variant="section">
        <CardContent className="space-y-4">
          <CreateUserForm />
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('admin.users.searchPlaceholder')}
              className="h-9 w-full rounded-md border bg-background pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring"
              autoComplete="off"
            />
          </div>

          {error && !data && <ServerStatusNotice variant="card" error={error} />}

          <UsersTable rows={results} loading={isLoading} t={t} />

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {total === 0
                ? t('admin.users.empty')
                : t('admin.users.pageOf', {
                    from: offset + 1,
                    to: Math.min(offset + PAGE_SIZE, total),
                    total,
                  })}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon-sm"
                disabled={!hasPrev || isLoading}
                onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                aria-label={t('admin.users.prev')}
              >
                <ChevronLeft className="size-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                disabled={!hasNext || isLoading}
                onClick={() => setOffset(o => o + PAGE_SIZE)}
                aria-label={t('admin.users.next')}
              >
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface TableProps {
  rows: AdminUserSummary[];
  loading: boolean;
  t: (k: string, p?: Record<string, string | number>) => string;
}

function UsersTable({ rows, loading, t }: TableProps) {
  const columns = useMemo(
    () => [
      { key: 'username', label: t('admin.users.colUsername') },
      { key: 'email', label: t('admin.users.colEmail') },
      { key: 'role', label: t('admin.users.colRole') },
      { key: 'branches', label: t('admin.users.colBranches') },
      { key: 'lastLogin', label: t('admin.users.colLastLogin') },
      { key: 'actions', label: '' },
    ],
    [t],
  );

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            {columns.map(c => (
              <th key={c.key} className="px-3 py-2 text-left font-semibold">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
                </span>
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                {t('admin.users.empty')}
              </td>
            </tr>
          )}
          {rows.map(u => (
            <tr key={u.id} className="border-t hover:bg-muted/30">
              <td className="px-3 py-2 font-medium">
                <Link
                  to={`/admin/users/${u.id}`}
                  className="hover:underline focus-visible:underline"
                >
                  {u.username}
                </Link>
              </td>
              <td className="px-3 py-2 text-muted-foreground">{u.email || '—'}</td>
              <td className="px-3 py-2">
                <RoleBadges user={u} t={t} />
              </td>
              <td className="px-3 py-2 tabular-nums text-muted-foreground">{u.zone_count}</td>
              <Tooltip content={u.last_login ?? undefined}><td className="px-3 py-2 tabular-nums text-muted-foreground whitespace-nowrap">
                {u.last_login ? formatLastLogin(u.last_login) : t('admin.users.neverLoggedIn')}
              </td></Tooltip>
              <td className="px-3 py-2 text-right">
                <Link
                  to={`/admin/users/${u.id}`}
                  className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
                >
                  <UserCog className="size-3.5" />
                  {t('admin.users.openProfile')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Последний вход (#7657): сегодня — «сегодня 14:05», в этом году — «24.10 14:05», раньше — с годом.
function formatLastLogin(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const hm = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return `сегодня ${hm}`;
  const opts: Intl.DateTimeFormatOptions = d.getFullYear() === now.getFullYear()
    ? { day: '2-digit', month: '2-digit' }
    : { day: '2-digit', month: '2-digit', year: 'numeric' };
  return `${d.toLocaleDateString('ru-RU', opts)} ${hm}`;
}

function RoleBadges({ user, t }: { user: AdminUserSummary; t: (k: string) => string }) {
  if (user.is_superuser) {
    return (
      <Badge variant="warning" className="gap-1">
        <ShieldCheck className="size-3" /> {t('admin.users.roleSuperuser')}
      </Badge>
    );
  }
  if (user.is_staff) {
    return <Badge variant="secondary">{t('admin.users.roleStaff')}</Badge>;
  }
  return <Badge variant="muted">{t('admin.users.roleMember')}</Badge>;
}

// «Создать пользователя» (Алексей 22.10, #1969: «нельзя добавить пользователя через Систему»).
// Пароль необязателен — тогда вход только через Telegram (привязка на странице пользователя).
function CreateUserForm() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!open) {
    return (
      <Button size="sm" className="gap-1.5" onClick={() => setOpen(true)}>
        <Plus className="size-4" /> Создать пользователя
      </Button>
    );
  }
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await createAdminUser({ username: username.trim(), email: email.trim() || undefined, password: password || undefined });
      navigate(`/admin/users/${r.data.id}`);
    } catch (e) {
      setErr(extractApiError(e, 'Не удалось создать пользователя'));
    } finally {
      setBusy(false);
    }
  };
  const field = 'h-9 rounded-md border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50';
  return (
    <div className="grid max-w-md gap-2 rounded-lg border p-3">
      <div className="text-sm font-medium">Новый пользователь</div>
      <input className={field} placeholder="Логин" value={username} onChange={e => setUsername(e.target.value)} autoComplete="off" />
      <input className={field} placeholder="Email (необязательно)" value={email} onChange={e => setEmail(e.target.value)} autoComplete="off" />
      <input className={field} type="password" placeholder="Пароль (необязательно — тогда вход через Telegram)" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
      {err && <p className="text-xs text-destructive">{err}</p>}
      <div className="flex gap-2">
        <Button size="sm" disabled={!username.trim() || busy} onClick={submit}>
          {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}Создать
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setErr(null); }}>Отмена</Button>
      </div>
      <p className="text-xs text-muted-foreground">После создания — выдать филиалы или добавить в группу на странице пользователя.</p>
    </div>
  );
}
